/** @module csi/widget/SelectList */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/query",
	"dojo/store/Memory",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/_WidgetBase",
	"dijit/registry",
	"underscore",
	"csi",
	"csi/html",
	"csi/locale",
	"csi/widget/CheckBox",
	"dojo/text!./SelectList.html",

	"dijit/form/TextBox"
], function(declare, domClass, query, Memory, _TemplatedMixin, _WidgetsInTemplateMixin, _WidgetBase, registry, _, csi, html, locale, CheckBox, template) {

	// TODO:
	// 1. make store asynchronous
	// 2. singleSelect does not work with useParentProperty

	/**
	 * @summary Filtered select list 
	 * Properties "store" and "value" must be set. Mix in to constructor or use .set('store', newStore), .set('value', newValue)
	   * Retrieve data by calling .get('value').
	   * 
	 * @example
	 *	var newList = new SelectList({
	 * 		value: newValue,
	 * 		store: newStore,
	 * 		'class': myCssClass
	 *	});
	 *
	   *  var result = newList.get('value');
	 */

	const ALL = "__SelectListAll__";
	const CSS_SELECTED = "csi-selected";

	return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {
		templateString: template,
		baseClass: 'csi-selectlist',

		/** @public @property {string} label - Select list label - located below filter but above options list */
		label: '',

		/** @public @property {number} maxHeight - maximum height of list (defaults to 300); reset to 0 for height to be automatic*/
		maxHeight: 300,

		/** @public @property {number} maxWidth - maximum width of items in the list (defaults to 0); also adds ellipsis and titles if non-zero */
		maxWidth: 0,

		/** @public @property {string} placeHolder - placeholder text for filter textbox */
		placeHolder: "",

		/** @public @property {boolean} preserveValueOrder - working if value is not null and selectedFirst is true, will preserver order of items in the list, according to supplied value */
		preserveValueOrder: false,

		/** @public @property {boolean} selectedFirst - Show selected items first */
		selectedFirst: true,

		/** @public @property {boolean} showAll - Show option to select all items */
		showAll: false,

		/** @public @property {boolean} showCheckmarks - Show check marks instead of check box. Which means: disable the frame around check box icon */
		showCheckmarks: false,

		/** @public @property {boolean} showFilter - Show filter textbox */
		showFilter: true,

		/** @public @property {boolean} singleSelect - If true, control works in single select mode, check boxes are not shown */
		singleSelect: false,

		/** @public @property {Memory} store - Do not access this directly. Mix in to constructor or use .set('store', newStore) */
		store: undefined,

		/** @public @property {boolean} touched - Return true if any of check boxes were clicked */
		touched: false,

		/** 
		 * @public @property {boolean} useParentProperty - set to true to use store's parentProperty to indent items with a parent;
		 * the current constraints in using this option is that there can only be one level of children (no grandparents),
		 * and parent items must exist in the store immediately before their children
		 */
		useParentProperty: false,

		/** @public @property {string[]} value - Do not access this directly. Mix in to constructor or use .set('value', newValue)/.get('value') */
		value: undefined,

		/** @private @property {CheckBox} _allCheckbox - "All" checkbox if showAll is true */
		_allCheckbox: null,

		/** @private @property {Object<string,CheckBox>} _checkboxMap - map of all checkboxes by id */
		_checkboxMap: null,

		/** @private @property {Object<string,string[]>} _childrenMap - populated when using useParentProperty to easily find the children of an item (reverse of parent) */
		_childrenMap: null,

		/** @private @property {Object<string,string[]>} _filteredChildrenMap - same as _childrenMap, except only contains filtered items */
		_filteredChildrenMap: null,

		/** @private @property {Object<string,boolean>} _selected - Object with selected values. Value id is used as key to the boolean property (checked/unchecked). */
		_selected: null,

		/** @private @property {Number} _timeoutId - Timeout handle. This timeout introduced to prevents flicker on multiple filter updates. */
		_timeoutId: null,

		/**
		 * Return true if item with given id is selected
		 * @param {string} id
		 * @return {boolean}
		 */
		isSelected: function(id) {
			return this._selected && this._selected[id];
		},

		/**
		 * Included so SelectList can be used in popup and prevent 
		 * ENTER key in text field from closing the popup (see popup.js)
		 * Code in popup.js checks if this method exists and if yes, it will trigger on "execute" event, if not, it uses "change" event instead
		 * If this method would not present here, default widget.onExecute handler will trigger on "change" event
		 * Default _HasDropDown handler calling closeDropDown(true) - this is what we trying to prevent here 
		 */
		onExecute: function() { },

		/**
		 * Extension point for onChange event
		 * @param {string[]} value
		 */
		onItemChange: function(value) { },

		postMixInProperties: function() {
			if (!this.placeHolder) {
				this.set("placeHolder", locale.resource("TypeKeywordToFilter"));
			}
			this.inherited(arguments);
		},

		/**
		 * Reset filter text
		 */
		resetFilter: function() {
			this.selectListEditorNode.set("value", "");
		},

		/**
		 * @override
		 * Release references to child checkboxes
		 */
		uninitialize: function() {
			this._checkboxMap = null;
			this.inherited(arguments);
		},

		/**
		 * Create _selected object.
		 */
		_createSelected: function() {
			const store = this.store;
			const selected = this._selected = {};
			const obj = csi.hashArray(this.value, null, Boolean);
			const parentProperty = this.useParentProperty && store.parentProperty;
			let childMap;

			if (parentProperty) {
				childMap = this._childrenMap = this._filteredChildrenMap = {};
			}

			store.query().forEach(item => {
				const id = store.getIdentity(item);
				selected[id] = !!obj[id];

				// also track parent-child relationships - the item has a parent reference, 
				// but we need to be able to easily get to the children of a given parent
				const parentId = item[parentProperty];
				if (parentId) {
					csi.objectArrayAddValue(childMap, parentId, id);
				}
			});
		},

		/**
		 * Create value array from selections
		 * @return {string[]}
		 */
		_createValueFromSelected: function() {
			const store = this.store;
			const value = [];

			store.query().forEach(item => {
				const id = store.getIdentity(item);

				if (this.isSelected(id)) {
					value.push(id);
				}
			});

			return value;
		},

		/*
		 * Return ALL constant
		 * @return {String}
		 */
		_getIdAll: function() {
			return ALL;
		},

		/**
		 * Set attribute "label"
		 * @param {string} val 
		 */
		_setLabelAttr: function(val) {
			this._set("label", val);
			this.selectListLabelNode.innerText = val;
			html.setVisible(this.selectListLabelNode, val);
			this._toggleContainerCss();
		},

		/**
		 * Set attribute "maxHeight"
		 * @param {number} val 
		 */
		_setMaxHeightAttr: function(val) {
			this._set("maxHeight", val);
			html.setPixels(this.domNode, html.MAXHEIGHT, val || "");
		},

		/**
		 * Set attribute "showCheckmark"
		 * @param {boolean} val 
		 */
		_setShowCheckmarksAttr: function(val) {
			this._set("showCheckmarks", val);
			domClass.toggle(this.domNode, 'csi-cb-checkmark', val);
		},

		/**
		 * Set attribute "showFilter"
		 * @param {boolean} val 
		 */
		_setShowFilterAttr: function(val) {
			this._set("showFilter", val);
			html.setVisible(this.selectFilterNode, val);
			domClass.toggle(this.selectListLabelNode, this.baseClass + "-label-with-editor", val);
			this._toggleContainerCss();
		},

		/**
		 * Set attribute "store". 
		 * Create _selected if both value and store are defind 
		 * @param {Memory} val 
		 */
		_setStoreAttr: function(val) {
			var store = val;
			var value = this.value;
			if (value && this.preserveValueOrder) {
				var idProperty = val.idProperty;
				var idx = value.length;
				var data = _.sortBy(_.clone(val.data), function(item) {
					var res = value.indexOf(item[idProperty]);
					return (res < 0) ? idx++ : res;
				});
				store = new Memory({data: data});
			}
			this._set("store", store);
			if (this.value && this.store) {
				this._createSelected();
				this._createItems();
			}
		},

		/**
		 * Set attribute "value". Preserve original value by creating a clone of the input array.
		 * Create _selected if both value and store are defined.
		 * @param {string[]} val 
		 */
		_setValueAttr: function(val) {
			if (this.singleSelect && (val.length > 1)) {
				console.assert(false, "At most one value can be selected");
				val = [val[0]];
			}

			this._set("value", val.slice(0));
			if (this.value && this.store) {
				this._createSelected();
				this._createItems();
			}
		},

		/**
		 * Change CSS for items container when either the label or filter options change
		 */
		_toggleContainerCss: function() {
			domClass.toggle(this.selectListItemsContainer, this.baseClass + "-items-container-with-sibling", !!this.label || !!this.showFilter);
		},

		/**
		 * Create option container
		 * @param {String[]} css
		 * @param {Sting} id
		 * @param {Sting} parentId
		 * @return {domNode}
		 */
		_createOptionContainer: function(css, id, parentId) {
			return html.create("div", {
				"class": css.join(" ")
			}, this.selectListItemsContainer);
		},

		/**
		 * Create single selection item
		 * @param {boolean} bMatrix
		 * @param {string} id
		 * @param {string} name
		 * @param {boolean} checked
		 * @param {string} [parentId]
		 * @param {string} [extraCss]
		 * @param {string} [bGroup]
		 * @return {CheckBox}
		 */
		_createOption: function(bMatrix, id, name, checked, parentId, extraCss, bGroup) {
			const baseClass = this.baseClass;
			const maxWidth = this.maxWidth;
			let css = [baseClass + "-item"];
			let disabled = false;

			if (extraCss) {
				css.push(extraCss);
			}

			if (checked && this.singleSelect) {
				css.push(CSS_SELECTED);
			}

			const div = this._createOptionContainer(css, id, parentId);
			if (bMatrix) {
				html.setDataAttribute(div, html.ATTR_LIST_MATRIX, bMatrix.toString());
			}
			if (bGroup) {
				html.setDataAttribute(div, html.ATTR_LIST_GROUP, bGroup.toString());
			}


			css = [baseClass + "-item-checkbox"];

			if (parentId) {
				css.push(baseClass + "-child-checkbox");
			}

			if (this.singleSelect) {
				css.push("nodisplay");
			}

			if (this.useParentProperty && !parentId) {
				// if this is a parent item, then disable it if all children are not displayed
				const allChildren = this._childrenMap[id];
				const displayedChildren = this._filteredChildrenMap[id];
				disabled = (_.size(allChildren) != _.size(displayedChildren));
			}

			const cb = new CheckBox({
				checked,
				disabled,
				"class": css.join(" "),
				csiId: id,
				csiParentId: parentId,
				onChange: checked => this._onCheckbox(div, cb, checked)
			}).placeAt(div);

			html.create("label", {
				"class": baseClass + "-item-label",
				"for": cb.id,
				innerText: name,
				style: maxWidth ? "max-width:" + maxWidth + "px" : "",
				title: maxWidth ? name : ""
			}, div);

			return cb;
		},

		/**
		 * Create filtering query that takes parent/child relationships into account
		 * @return {Function|null}
		 */
		_createFilterQuery: function() {
			const filterText = this.selectListEditorNode.get('value');

			if (!filterText) {
				return null;
			}

			const store = this.store;
			const childMap = this._childrenMap;
			const nameProperty = this._getNameProperty();
			const parentProperty = this.useParentProperty && store.parentProperty;
			const re = new RegExp(filterText, 'i');

			return item => {
				const name = item[nameProperty];

				if (re.test(name)) {
					return true;
				}

				if (parentProperty) {
					const parentId = item[parentProperty];
					const parent = parentId && store.get(parentId);
					const children = childMap[store.getIdentity(item)];

					if (parent) {
						// child should be included if parent passes filter
						return re.test(parent[nameProperty]);
					} else if (children) {
						// parent should be included if any child passes filter
						return _.some(children, childId => {
							const child = store.get(childId);
							return re.test(child[nameProperty]);
						});
					}
				}

				return false;
			};
		},

		/**
		 * Create single selection item
		 * @param {Object} item
		 */
		_createItem: function(item) {
			const {bMatrix, bGroup} = item;
			const name = item[this._getNameProperty()];
			const id = this.store.getIdentity(item);
			const checked = this.isSelected(id);
			const parent = this.useParentProperty ? item[this.store.parentProperty] : null;
			this._checkboxMap[id] = this._createOption(bMatrix, id, name, checked, parent, "", bGroup);
		},

		/**
		 * Query store for available items and create them
		 */
		_createItems: function() {
			const filter = this._createFilterQuery();
			const rootNode = this.selectListItemsContainer;
			const store = this.store;
			const items = store.query(filter);

			html.destroyChildren(rootNode);
			this._checkboxMap = {};

			// apply CSS when displaying filtered results
			domClass.toggle(rootNode, this.baseClass + "-filter", !!filter);

			// add All checkbox, but doesn't make sense in singleSelect mode
			if (this.showAll) {
				console.assert(!this.singleSelect);

				const allSelected = _.every(this._selected, Boolean);
				this._allCheckbox = this._createOption(false, ALL, locale.resource("All"), allSelected, null, this.baseClass + "-all");

				html.create("div", {
					className: this.baseClass + "-all-separator"
				}, rootNode);
			}

			if (this.selectedFirst) {
				// since some children could be selected, and others not, it does not make sense to split them up (parent would need to be shown twice)
				console.assert(!this.useParentProperty);

				// show selected items first, in the order they present in value
				const groups = _.groupBy(items, item => this.isSelected(store.getIdentity(item), true));

				_.each(groups[true], this._createItem, this);
				_.each(groups[false], this._createItem, this);
			} else {
				if (this.useParentProperty) {
					if (filter) {
						// compile filtered map of parent to child items
						const parentProperty = store.parentProperty;
						const childMap = this._filteredChildrenMap = {};

						_.each(items, item => {
							const parentId = item[parentProperty];
							if (parentId) {
								csi.objectArrayAddValue(childMap, parentId, store.getIdentity(item));
							}
						});
					} else {
						// if no filtering, then filtered map is same as child map
						this._filteredChildrenMap = this._childrenMap;
					}
				}

				// create options for all items
				_.each(items, this._createItem, this);
			}
		},

		/**
		 * Return name property for store
		 * @return {string}
		 */
		_getNameProperty: function() {
			return this.store.nameProperty || "name";
		},

		/**
		 * Process change to checkbox
		 * @param {DOMNode} itemContainer
		 * @param {CheckBox} checkbox
		 * @param {boolean} checked
		 */
		_onCheckbox: function(itemContainer, checkbox, checked) {
			const id = checkbox.csiId;
			const parentId = checkbox.csiParentId;
			let value;

			this.touched = true;

			if (this.singleSelect) {
				this._selected = {};
				this._selected[id] = true;
				value = [id];
				$(this.selectListItemsContainer).find(".csi-selected").toggleClass(CSS_SELECTED, false);
				domClass.toggle(itemContainer, CSS_SELECTED, true);
			} else {
				const selected = this._selected;
				const all = this._allCheckbox;

				if (id === ALL) {
					// update all checkboxes that do not match the All checkbox state
					_.each(this._checkboxMap, (cb, id) => {
						if (cb.get("checked") !== checked) {
							cb.set("checked", checked, false);
							selected[id] = checked;
						}
					});
				} else {
					selected[id] = checked;

					// update parent or children items
					if (this.useParentProperty) {
						const children = this._filteredChildrenMap[id];

						if (parentId) {
							// update checked status for parent
							const allChildren = this._childrenMap[parentId];
							const parentChecked = _.some(allChildren, childId => selected[childId]);
							const parentCheckbox = this._checkboxMap[parentId];
							parentCheckbox.set("checked", parentChecked, false);
							selected[parentId] = parentChecked;
						} else if (children) {
							// apply checked status to all children
							_.each(children, childId => {
								const childCheckbox = this._checkboxMap[childId];
								childCheckbox.set("checked", checked, false);
								selected[childId] = checked;
							});
						}
					}

					// update All checkbox if necessary
					if (all) {
						if (!checked) {
							all.set("checked", false, false);
						} else if (_.every(selected, Boolean)) {
							all.set("checked", true, false);
						}
					}
				}

				value = this._createValueFromSelected();
			}

			this._set("value", value);
			this.onItemChange(value);
		},

		/**
		 * Process filter textbox change event
		 * @param {Event} e
		 */
		_onFilterChange: function(e) {
			clearTimeout(this._timeoutId);
			this._timeoutId = setTimeout(() => this._createItems(), 300);
		},

		/**
		 * Handle mouse click on item row outside of check box and label and deliver the click to check box
		 * @param {Event} e 
		 */
		_onItemClick: function(e) {
			var elem = query('.dijitCheckBoxInput', e.target);
			if (elem.length) { // query result will be empty, if you click on checkbox or label.
				var cb = registry.byId(elem[0].id);
				if (cb) {
					cb.set('checked', !cb.checked);
				}
			}
		}
	});
});